import { EditableInput } from '@/components/editableInput';
import { Checkbox } from '@tigergraph/app-ui-lib/checkbox';
import { StyleObject } from 'styletron-standard';
import { CustomTheme } from '@tigergraph/app-ui-lib/Theme';
import {
  EmbeddingAttribute,
  supportedEmbeddingAttributeMaxDimensions,
} from '@tigergraph/tools-models/topology/embedding-attribute.model';
import EmbeddingAttributeMetricSelect from '@/components/schemaDesigner/attributeEditorContainer/attributeEditor/EmbeddingAttributeMetricSelect';
import EmbeddingAttributeIndexTypeSelect from '@/components/schemaDesigner/attributeEditorContainer/attributeEditor/EmbeddingAttributeIndexTypeSelect';
import EmbeddingAttributeDataTypeSelect from '@/components/schemaDesigner/attributeEditorContainer/attributeEditor/EmbeddingAttributeDataTypeSelect';

export interface EmbeddingAttributeEditorRowProps {
  css: (arg: StyleObject) => string;
  theme: CustomTheme;
  attribute: EmbeddingAttribute;
  deleting: boolean;
  readOnly?: boolean;
  handleAttributeChange: (attribute: EmbeddingAttribute) => void;
  handleDeleteSelection: () => void;
}

export function buildEmbeddingAttributeEditorRow(props: EmbeddingAttributeEditorRowProps) {
  const { css, theme, attribute, deleting, readOnly, handleAttributeChange, handleDeleteSelection } = props;

  const handleAttributeNameChange = (name: string) => {
    const newAttribute = attribute.clone();
    newAttribute.name = name;
    handleAttributeChange(newAttribute);
  };

  const handleAttributeDimensionChange = (dimension: string | number) => {
    const newAttribute = attribute.clone();
    newAttribute.dimension = Number(dimension);
    handleAttributeChange(newAttribute);
  };

  const handleAttributeMetricChange = (metric: string) => {
    const newAttribute = attribute.clone();
    newAttribute.metric = metric;
    handleAttributeChange(newAttribute);
  };

  const handleAttributeIndexTypeChange = (indexType: string) => {
    const newAttribute = attribute.clone();
    newAttribute.indexType = indexType;
    handleAttributeChange(newAttribute);
  };

  const handleAttributeDataTypeChange = (dataType: string) => {
    const newAttribute = attribute.clone();
    newAttribute.type = dataType;
    handleAttributeChange(newAttribute);
  };

  const row = [
    <div
      key={`delete_checkbox_${attribute.name}`}
      className={css({
        display: 'flex',
        alignItems: 'center',
        lineHeight: '35px',
        height: '35px',
      })}
    >
      <Checkbox checked={deleting} disabled={readOnly} onChange={() => handleDeleteSelection()} />
    </div>,
    <div
      key={`Embedding_attribute_name_${attribute.name}`}
      className={css({
        minWidth: 'fit-content',
        display: 'flex',
        height: '32px',
        lineHeight: '32px',
        alignItems: 'center',
        columnGap: '4px',
        fontSize: '12px',
      })}
    >
      <EditableInput
        value={attribute.name}
        placeholder="Attribute name"
        readOnly={readOnly}
        onChange={(name) => handleAttributeNameChange(String(name).trim())}
      />
    </div>,
    <div key={`Embedding_attribute_dimension_${attribute.name}`}>
      <EditableInput
        value={attribute.dimension}
        placeholder="Attribute dimension"
        type="number"
        width="80px"
        min={1}
        max={supportedEmbeddingAttributeMaxDimensions}
        readOnly={readOnly}
        onChange={handleAttributeDimensionChange}
      />
    </div>,
    <div key={`Embedding_attribute_metric_${attribute.name}`}>
      <EmbeddingAttributeMetricSelect
        metric={attribute.metric}
        readOnly={readOnly}
        onChange={handleAttributeMetricChange}
      />
    </div>,
    <div key={`Embedding_attribute_index_type_${attribute.name}`}>
      <EmbeddingAttributeIndexTypeSelect
        indexType={attribute.indexType}
        readOnly={true}
        onChange={handleAttributeIndexTypeChange}
      />
    </div>,
    <div key={`Embedding_attribute_data_type_${attribute.name}`}>
      <EmbeddingAttributeDataTypeSelect
        dataType={attribute.type}
        readOnly={true}
        onChange={handleAttributeDataTypeChange}
      />
    </div>,
  ];

  return row;
}
